import { Alert, Button, Card, Form, Input, Popconfirm, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import type { ActivityPageResult, ActivityQuery, ActivityRow, ActivitySpecial } from "./types";

// 沿用 Vue useTable 的商品活动列表默认分页大小。
const defaultQuery: ActivityQuery = { page: 1, size: 10 };
const activityStateLabels: Record<number, string> = { 0: "已关闭", 1: "已开启", 2: "草稿" };
const activityTypeLabels: Record<number, string> = { 1: "活动报名", 2: "活动商品" };
type ActivityFilterValues = ActivityQuery & { createdTime?: [string, string] | null };

/** 按旧 Vue 列表的 DATETIME_PICKER_FORMAT 展示活动时间字段。 */
const formatActivityDateTime = (value?: string) => (value ? dateFormat(value, DATETIME_PICKER_FORMAT) : "-");

/** 对齐 Vue showOpenBtn：只有已关闭且当前时间处于活动有效期内才允许开启。 */
function isActivityOpenable(row: ActivityRow): boolean {
    const start = row.campaign.validTimeStart ? Date.parse(row.campaign.validTimeStart) : Number.NaN;
    const end = row.campaign.validTimeFinish ? Date.parse(row.campaign.validTimeFinish) : Number.NaN;
    return Number.isFinite(start) && Number.isFinite(end) && start < Date.now() && end > Date.now();
}

export interface ActivityPageProps {
    assignActivity?: (id: number | string, handlerId: number | string) => Promise<void>;
    closeActivity?: (id: number | string) => Promise<void>;
    deleteActivity?: (id: number | string) => Promise<void>;
    loadActivities?: (query: ActivityQuery) => Promise<ActivityPageResult>;
    loadSpecials?: (keyword: string) => Promise<ActivitySpecial[]>;
    openActivity?: (id: number | string) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 活动分页接口。 */
export async function loadActivities(query: ActivityQuery): Promise<ActivityPageResult> {
    const response = await request.service.get<ActivityPageResult>("/admin/campaign/paging", { params: query });
    return response.data;
}

/** 复用 Vue 草稿活动删除接口。 */
export async function deleteActivity(id: number | string): Promise<void> {
    await request.service.delete(`/admin/campaign/delete/${id}`);
}

/** 复用 Vue 活动开启接口。 */
export async function openActivity(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/open/${id}`);
}

/** 复用 Vue 活动关闭接口。 */
export async function closeActivity(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/close/${id}`);
}

/** 复用 Vue 活动专员查询接口。 */
export async function loadActivitySpecials(keyword: string): Promise<ActivitySpecial[]> {
    const response = await request.service.get<ActivitySpecial[]>(
        `/admin/campaign/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 活动专员指派接口。 */
export async function assignActivity(id: number | string, handlerId: number | string): Promise<void> {
    await request.service.get(`/admin/campaign/assign/${id}/${handlerId}`);
}

/** 将 Vue 活动分页返回的状态和内容配置枚举统一为 React 列表使用的数字。 */
function normalizeActivityRows(rows?: ActivityRow[]): ActivityRow[] {
    return (rows || []).map(row => ({
        ...row,
        campaign: {
            ...row.campaign,
            state: row.campaign.state == null ? undefined : Number(row.campaign.state),
            contentConfigs: row.campaign.contentConfigs?.map(config => Number(config))
        }
    }));
}

/** 商品活动列表，保留 Vue 首屏字段、权限和草稿删除动作。 */
export function ActivityPage({
    assignActivity: assign = assignActivity,
    closeActivity: close = closeActivity,
    deleteActivity: remove = deleteActivity,
    loadActivities: load = loadActivities,
    loadSpecials: loadSpecialsForActivity = loadActivitySpecials,
    openActivity: open = openActivity,
    permissions
}: ActivityPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_ACTI_W");
    const [items, setItems] = useState<ActivityRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<ActivityQuery>(defaultQuery);
    const [filterForm] = Form.useForm<ActivityFilterValues>();
    const [assignTarget, setAssignTarget] = useState<ActivityRow | null>(null);
    const [specials, setSpecials] = useState<ActivitySpecial[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [specialKeyword, setSpecialKeyword] = useState("");

    const refresh = async (targetQuery: ActivityQuery = query) => {
        const result = await load(targetQuery);
        setItems(normalizeActivityRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeActivityRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("活动加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将 Vue 表格筛选条件转换为后端约定的分页查询参数。
    const submitFilters = (values: ActivityFilterValues) => {
        const { createdTime, ...rawFilters } = values;
        const [createdTimeStart, createdTimeEnd] = createdTime || [];
        if (createdTimeStart && createdTimeEnd && createdTimeEnd < createdTimeStart) {
            setErrorMessage("创建结束日期不能早于开始日期");
            return;
        }
        const filters = Object.fromEntries(
            Object.entries(rawFilters).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ) as Omit<ActivityQuery, "page" | "size">;
        if (createdTimeStart) filters.startTime = `${createdTimeStart} 00:00:00`;
        if (createdTimeEnd) filters.endTime = `${createdTimeEnd} 23:59:59`;
        if (filters.state !== undefined) filters.state = Number(filters.state);
        setErrorMessage(null);
        setQuery({ page: 1, size: 10, ...filters });
    };

    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    const handleDelete = async (id?: number | string) => {
        if (id == null) return;
        try {
            await remove(id);
            await refresh();
        } catch {
            // 草稿活动删除失败时保留当前列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("活动删除失败");
        }
    };

    // 开启或关闭成功后重新加载活动分页，保持列表状态与服务端一致。
    const handleOpen = async (id?: number | string) => {
        if (id == null) return;
        try {
            await open(id);
            await refresh();
        } catch {
            setErrorMessage("活动开启失败");
        }
    };

    const handleClose = async (id?: number | string) => {
        if (id == null) return;
        try {
            await close(id);
            await refresh();
        } catch {
            setErrorMessage("活动关闭失败");
        }
    };

    const showAssign = async (row: ActivityRow) => {
        if (row.campaign.id == null) return;
        try {
            setSpecialKeyword("");
            setSpecials(await loadSpecialsForActivity(""));
            setSelectedHandlerId(row.campaign.handlerId == null ? "" : String(row.campaign.handlerId));
            setAssignTarget(row);
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const searchSpecials = async () => {
        try {
            setSpecials(await loadSpecialsForActivity(specialKeyword));
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const submitAssign = async () => {
        if (assignTarget?.campaign.id == null || !selectedHandlerId) return;
        try {
            await assign(assignTarget.campaign.id, Number(selectedHandlerId));
            setAssignTarget(null);
            await refresh();
        } catch {
            setErrorMessage("活动指派失败");
        }
    };

    // 将 Vue 活动列表的更新时间排序转换为活动分页接口字段，并回到第一页。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (prop === "updatedTime" && order) {
                nextQuery.sortKey = "updated_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            } else if (prop === "createdTime" && order) {
                nextQuery.sortKey = "created_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接 Vue 活动列表的字段、状态和专员操作。
    const columns: TableColumnProps<ActivityRow>[] = [
        {
            key: "code",
            label: "活动编号",
            prop: "campaign",
            render: ({ row }) => (
                <Button
                    aria-label="查看活动详情"
                    type="link"
                    onClick={() =>
                        void navigate(
                            `/productActivity/activity/detail?id=${row.campaign.code || ""}&campaignId=${row.campaign.id || ""}`
                        )
                    }
                >
                    {row.campaign.code || "-"}
                </Button>
            )
        },
        { key: "title", label: "活动标题", prop: "campaign", render: ({ row }) => row.campaign.title || "-" },
        {
            key: "validTime",
            label: "活动时间范围",
            prop: "campaign",
            render: ({ row }) =>
                row.campaign.validTimeStart && row.campaign.validTimeFinish
                    ? `${formatActivityDateTime(row.campaign.validTimeStart)}至${formatActivityDateTime(row.campaign.validTimeFinish)}`
                    : "-"
        },
        { key: "quantity", label: "商品数量(个)", prop: "quantity", render: ({ row }) => row.quantity ?? "-" },
        {
            key: "contentConfigs",
            label: "内容配置",
            prop: "campaign",
            render: ({ row }) =>
                row.campaign.contentConfigs?.map(value => activityTypeLabels[value] || value).join("、") || "-"
        },
        {
            key: "handlerName",
            label: "活动专员",
            prop: "campaign",
            render: ({ row }) => row.campaign.handlerName || "-"
        },
        {
            key: "state",
            label: "活动状态",
            prop: "campaign",
            render: ({ row }) => <Tag>{activityStateLabels[row.campaign.state ?? -1] || "-"}</Tag>
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "updatedTime",
            sortable: "custom",
            render: ({ row }) => formatActivityDateTime(row.campaign.updatedTime)
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "createdTime",
            sortable: "custom",
            render: ({ row }) => formatActivityDateTime(row.campaign.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            prop: "campaign",
            render: ({ row }) => {
                if (!canWrite) return null;
                return (
                    <Space>
                        <Button
                            aria-label={`变更指派活动${row.campaign.id || ""}`}
                            size="small"
                            onClick={() => void showAssign(row)}
                        >
                            变更指派
                        </Button>
                        <Button
                            aria-label="编辑活动"
                            size="small"
                            onClick={() =>
                                void navigate(
                                    `/productActivity/activity/edit?id=${row.campaign.code || ""}&campaignId=${row.campaign.id || ""}`
                                )
                            }
                        >
                            编辑
                        </Button>
                        {row.campaign.state === 1 ? (
                            <Button
                                aria-label={`关闭活动${row.campaign.id || ""}`}
                                size="small"
                                onClick={() => void handleClose(row.campaign.id)}
                            >
                                关闭
                            </Button>
                        ) : null}
                        {row.campaign.state === 0 && isActivityOpenable(row) ? (
                            <Button
                                aria-label={`开启活动${row.campaign.id || ""}`}
                                size="small"
                                onClick={() => void handleOpen(row.campaign.id)}
                            >
                                开启
                            </Button>
                        ) : null}
                        {row.campaign.state === 2 ? (
                            <Popconfirm
                                cancelText="取消"
                                okText="确定"
                                title="确认删除该活动吗？"
                                onConfirm={() => void handleDelete(row.campaign.id)}
                            >
                                <Button aria-label="删除活动" danger size="small">
                                    删除
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="activity-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ActivityRow>
                    data={items}
                    columns={columns}
                    emptyText="暂无活动"
                    onSortChange={handleSortChange}
                    pagination={false}
                    rowKey={row => String(row.campaign.id || row.campaign.code)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                活动列表
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="mb-4"
                                form={filterForm}
                                initialValues={{ state: "" }}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                <Form.Item label="活动编号" name="code">
                                    <Input placeholder="请输入活动编号搜索" />
                                </Form.Item>
                                <Form.Item label="活动标题" name="title">
                                    <Input placeholder="请输入活动标题搜索" />
                                </Form.Item>
                                <Form.Item label="活动专员" name="handlerName">
                                    <Input placeholder="请输入活动专员" />
                                </Form.Item>
                                <Form.Item label="活动状态" name="state">
                                    <select aria-label="活动状态">
                                        <option value="">全部状态</option>
                                        <option value="0">已关闭</option>
                                        <option value="1">已开启</option>
                                        <option value="2">草稿</option>
                                    </select>
                                </Form.Item>
                                <Form.Item label="创建时间" name="createdTime">
                                    <ZhDateRangePicker
                                        endPlaceholder="创建结束日期"
                                        format="YYYY-MM-DD"
                                        startPlaceholder="创建开始日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Space>
                                    <Button htmlType="submit" type="primary">
                                        查询活动
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>

                            {canWrite ? (
                                <div className="mb-4">
                                    <Button
                                        type="primary"
                                        onClick={() => void navigate("/productActivity/activity/add")}
                                    >
                                        新增活动
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 分页参数，切页保留筛选条件；切换页大小时回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={Boolean(assignTarget)}
                title="指派专员"
                width="842px"
                onCancel={() => setAssignTarget(null)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignTarget(null)}>取消</Button>
                        <Button onClick={() => void submitAssign()} type="primary">
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <Space orientation="vertical" className="w-full">
                    <Input
                        aria-label="专员姓名"
                        placeholder="请输入专员姓名"
                        value={specialKeyword}
                        onChange={event => setSpecialKeyword(event.target.value)}
                    />
                    <Button onClick={() => void searchSpecials()}>搜索专员</Button>
                    <select
                        aria-label="活动专员"
                        value={selectedHandlerId}
                        onChange={event => setSelectedHandlerId(event.target.value)}
                    >
                        <option value="">请选择活动专员</option>
                        {specials.map(special => (
                            <option key={String(special.id)} value={String(special.id)}>
                                {special.name}
                            </option>
                        ))}
                    </select>
                </Space>
            </ZhDialog>
        </section>
    );
}

export default ActivityPage;
